
import { Permission } from './types';
import { createLogger } from "@/utils/logger";

const logger = createLogger('location:permissions');

// Cache permission results to avoid rapid re-checks
const permissionCache = {
  timestamp: 0,
  permission: null as Permission | null,
  coordinates: null as GeolocationCoordinates | null,
  expiresIn: 300000 // Increased from 60000 (1 min) to 300000 (5 mins)
};

/**
 * Check browser permission status with improved stability and caching
 */
export const checkBrowserPermission = async (): Promise<Permission> => {
  // Return cached result if recent
  const now = Date.now();
  if (permissionCache.permission && now - permissionCache.timestamp < permissionCache.expiresIn) {
    logger.debug(`Using cached permission: ${permissionCache.permission} (${Math.round((now - permissionCache.timestamp)/1000)}s old)`);
    return permissionCache.permission;
  }

  try {
    logger.debug('Checking permission using Permissions API');
    
    if (navigator.permissions) {
      // Use native permissions API with proper error handling
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
        .catch(err => {
          logger.warn('Error in permissions query:', err);
          return { state: 'prompt' } as PermissionStatus;  // Fallback
        });
      
      logger.debug(`Permission query result: ${permissionStatus.state}`);
      
      // Double-check if permission is granted but geolocation doesn't work
      if (permissionStatus.state === 'granted') {
        try {
          // Try a quick permission verification with proper timeouts
          const permissionConfirmed = await new Promise<boolean>((resolve) => {
            const timeoutId = setTimeout(() => {
              logger.debug('Permission verification timed out, assuming granted');
              resolve(true);
            }, 1500); // Increased from 1000 to 1500
            
            navigator.geolocation.getCurrentPosition(
              () => {
                clearTimeout(timeoutId);
                resolve(true);
              },
              (error) => {
                clearTimeout(timeoutId);
                if (error.code === 1) { // 1 is PERMISSION_DENIED
                  logger.debug('Permission API reports granted but getCurrentPosition failed with permission denied');
                  resolve(false);
                } else {
                  resolve(true); // Other errors but permission is granted
                }
              },
              { timeout: 1200, maximumAge: 300000 } // Increased timeout and maximumAge
            );
          });
          
          if (!permissionConfirmed) {
            logger.debug('Verification failed, returning denied');
            cachePermissionResult('denied');
            return 'denied';
          }
        } catch (err) {
          logger.debug('Error in permission verification test, trusting permissions API');
        }
      }
      
      const result = permissionStatus.state as Permission;
      cachePermissionResult(result);
      return result;
    }
    
    logger.debug('navigator.permissions not available, using fallback');
  } catch (error) {
    logger.debug('Error checking geolocation permission using Permissions API:', error);
    logger.debug('Falling back to getCurrentPosition probe');
  }
  
  // Better fallback with timeout and reliable error handling
  const result = await new Promise<Permission>((resolve) => {
    if (!navigator.geolocation) {
      logger.debug('Geolocation API not supported');
      cachePermissionResult('denied');
      resolve('denied');
      return;
    }
    
    const timeoutId = setTimeout(() => {
      logger.debug('Permission check timed out, assuming prompt');
      cachePermissionResult('prompt');
      resolve('prompt');
    }, 1500); // Increased from 800 to 1500 for better reliability
    
    try {
      logger.debug('Probing for permission using getCurrentPosition');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          logger.debug('Permission check successful, permission is granted');
          
          // Also cache the coordinates for potential future use
          if (position && position.coords) {
            permissionCache.coordinates = position.coords;
          }
          
          cachePermissionResult('granted');
          resolve('granted');
        },
        (error) => {
          clearTimeout(timeoutId);
          if (error.code === 1) { // PERMISSION_DENIED
            logger.debug('Permission explicitly denied by user');
            cachePermissionResult('denied');
            resolve('denied');
          } else {
            logger.debug(`Position error (code ${error.code}), assuming prompt`);
            cachePermissionResult('prompt');
            resolve('prompt');
          }
        },
        { timeout: 1500, maximumAge: 300000 } // Increased timeout and maximumAge
      );
    } catch (e) {
      clearTimeout(timeoutId);
      logger.debug('Error during permission check fallback:', e);
      cachePermissionResult('prompt');
      resolve('prompt');
    }
  });
  
  return result;
};

/**
 * Cache permission result to avoid excessive API calls
 */
function cachePermissionResult(permission: Permission): void {
  permissionCache.permission = permission;
  permissionCache.timestamp = Date.now();
  logger.debug(`Cached permission result: ${permission}`);
}

/**
 * Get current position with better error handling and timeout management
 */
export const getCurrentPosition = (
  onStartLoading: () => void,
  onFinishLoading: () => void
): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    
    onStartLoading();
    
    // Use cached coordinates if we have them and they're recent
    if (permissionCache.coordinates && 
        Date.now() - permissionCache.timestamp < 120000) { // Increased to 2 minutes
      logger.debug('Using cached coordinates as they are recent');
      setTimeout(() => {
        onFinishLoading();
        resolve({
          coords: permissionCache.coordinates as GeolocationCoordinates,
          timestamp: permissionCache.timestamp
        } as GeolocationPosition);
      }, 100); // Small delay to simulate async operation for consistent behavior
      return;
    }
    
    const timeoutId = setTimeout(() => {
      onFinishLoading();
      reject(new Error('Geolocation request timed out'));
    }, 12000); // Increased from 10000 to 12000 for better reliability
    
    navigator.geolocation.getCurrentPosition(
      position => {
        clearTimeout(timeoutId);
        onFinishLoading();
        
        // Cache the result
        permissionCache.coordinates = position.coords;
        permissionCache.timestamp = Date.now();
        
        resolve(position);
      },
      error => {
        clearTimeout(timeoutId);
        onFinishLoading();
        
        // Clear any cached coordinates on error
        if (error.code === 1) { // Permission denied
          permissionCache.coordinates = null;
          permissionCache.permission = 'denied';
          permissionCache.timestamp = Date.now();
        }
        
        reject(error);
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000, // Increased from 8000 to 10000
        maximumAge: 10 * 60 * 1000 // Increased from 5 to 10 minutes
      }
    );
  });
};

/**
 * Check if we have valid coordinates
 */
export const hasValidCoordinates = (coords: any): boolean => {
  return !!(coords && 
    typeof coords.lat === 'number' && 
    typeof coords.lng === 'number' &&
    !isNaN(coords.lat) && 
    !isNaN(coords.lng) && 
    Math.abs(coords.lat) <= 90 && 
    Math.abs(coords.lng) <= 180
  );
};

/**
 * Verify if coords are valid and accessible
 */
export const verifyLocationAccess = async (): Promise<boolean> => {
  try {
    const permission = await checkBrowserPermission();
    return permission === 'granted';
  } catch (err) {
    logger.warn('Error verifying location access:', err);
    return false;
  }
};
